import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useMemo } from 'react';

import queryKeys from '@pray/shared/queryKeys';
import { evaluateFeatureFlags } from '@pray/shared/services/studioService/featureFlags';
import type { EvaluateFlagsResult } from '@pray/shared/services/studioService/featureFlags';

import { useStudioContext } from './StudioContext';

type FeatureFlagContextValue = {
  flags: EvaluateFlagsResult;
  /**
   * True once the evaluate call has settled for the current artist. While false,
   * `flags` is still the empty default — gates that redirect (rather than just
   * hide) should wait for this instead of treating "not loaded yet" as "denied".
   */
  isReady: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({ flags: {}, isReady: false });

export default function FeatureFlagProvider({
  flagIds = [],
  children,
}: {
  flagIds?: string[];
  children: React.ReactNode;
}) {
  const { selectedArtist } = useStudioContext();
  const artistId = selectedArtist?.id;

  const { data, isFetched } = useQuery({
    queryKey: queryKeys.featureFlagEvaluate('studio', 'studio', artistId),
    queryFn: () =>
      evaluateFeatureFlags({
        flag_ids: flagIds,
        context: {
          platform: 'studio',
          app_type: 'studio',
          artist_id: artistId || undefined,
        },
      }),
    enabled: !!artistId && flagIds.length > 0,
    staleTime: 30_000,
  });

  const value = useMemo<FeatureFlagContextValue>(() => {
    return { flags: data?.data?.flags ?? {}, isReady: isFetched };
  }, [data, isFetched]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlagContext(): FeatureFlagContextValue {
  return useContext(FeatureFlagContext);
}
